import BankTransaction from "../services/BankTransaction";

type OperationType = BankTransaction.TripReportOperationType;

type Source = Pick<
	BankTransaction.Model,
	| "id"
	| "status"
	| "remountTransactionType"
	| "amount"
	| "createdAt"
	| "tripReport"
>;

function toIso(value?: Date | string | null): string | undefined {
	if (!value) return undefined;
	const parsed = value instanceof Date ? value : new Date(value);
	if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
	const text = String(value).trim();
	return text || undefined;
}

function toAmount(value?: number | string | null): number | undefined {
	if (value == null || value === "") return undefined;
	const amount = Number(value);
	return Number.isFinite(amount) ? amount : undefined;
}

function toOperationType(remount?: string | null): OperationType | null {
	switch (remount) {
		case BankTransaction.RemountTransactionType.HOLD:
		case BankTransaction.RemountTransactionType.AUTH:
			return "hold";
		case BankTransaction.RemountTransactionType.CAPTURE:
			return "capture";
		case BankTransaction.RemountTransactionType.WITHDRAW:
			return "withdraw";
		case BankTransaction.RemountTransactionType.REFUND:
		case BankTransaction.RemountTransactionType.AUTH_REFUND:
			return "refund";
		default:
			return null;
	}
}

function isReportable(transaction: Source): boolean {
	if (
		transaction.status &&
		transaction.status !==
			BankTransaction.PaymentTransactionStatus.COMPLETED
	) {
		return false;
	}
	const type = toOperationType(transaction.remountTransactionType);
	if (!type) return false;
	if (transaction.tripReport) return true;
	return type === "refund";
}

/**
 * Собирает отчёт из всех карточных BT заказа.
 * Capture с той же суммой, что холд, скрываем — в чеке холд, затем
 * досписание или возврат.
 */
export function composeTripReportFromTransactions(
	transactions: Source[],
): BankTransaction.TripReport | null {
	const relevant = [...transactions]
		.filter(isReportable)
		.sort((left, right) => left.id - right.id);

	const header = relevant
		.map((transaction) => transaction.tripReport)
		.find(Boolean);
	if (!header) return null;

	const mapped = relevant.flatMap((transaction) => {
		const type = toOperationType(transaction.remountTransactionType);
		if (!type) return [];

		const snapshot = transaction.tripReport;
		return [
			{
				type,
				transactionDate:
					snapshot?.transactionDate || toIso(transaction.createdAt),
				amount: toAmount(snapshot?.amount ?? transaction.amount),
				currency: snapshot?.currency || header.currency,
			},
		];
	});

	const hold = mapped.find((operation) => operation.type === "hold");
	const operations = mapped
		.filter((operation) => {
			if (operation.type !== "capture") return true;
			if (!hold) return true;
			return Number(hold.amount) !== Number(operation.amount);
		})
		.map((operation) => {
			if (operation.type !== "hold") return operation;
			return {
				...operation,
				rrn: header.rrn,
				authorizationCode: header.authorizationCode,
			};
		});
	if (!operations.length) return null;

	const last = operations[operations.length - 1];
	return {
		...header,
		amount: last.amount ?? header.amount,
		transactionDate: last.transactionDate ?? header.transactionDate,
		currency: last.currency ?? header.currency,
		operations,
	};
}

export default composeTripReportFromTransactions;
